import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { useHistory } from 'react-router-dom';
import { DATE_FORMAT_DEFAUT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import dayjs from 'dayjs';
import DriversService from 'api/drivers/DriversService';
import UsersService from 'api/users/UsersService';
import { DriversSearchCriteria } from 'api/drivers/models/DriversSearchCriteria';
import { DriversTotalsDto } from 'api/drivers/models/DriversTotalsDto';
import Logger from 'common/services/Logger';
import Loading from 'common/services/Loading';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Tabs, { TabItem } from 'common/components/tabs/Tabs';
import DriverChargeTab from './components/driverChargeTab/DriverChargeTab';
import DriverAccidentTab from './components/driverAccidentTab/DriverAccidentTab';
import { Types } from 'screens/vehicle/VehicleScreen';
import DriverUserTab from './components/driverUserTab/DriverUserTab';
import Popover from 'common/components/popover/Popover';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import CoinsIcon from 'assets/svg/coins.svg';
import TicketIcon from 'assets/svg/ticket_white.svg';
import ConeIcon from 'assets/svg/cone.svg';
import DriversFilters, { Filters as FiltersDriversModel } from '../drivers/components/DriversFilters';
import styles from './DriverScreen.module.scss';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import ChargeFiltersScreen, { Filters as FiltersChargesModel } from '../charges/components/ChargeFiltersScreen';
import { ChargesSearchCriteria } from 'api/charges/models/ChargesSearchCriteria';
import { AccidentsSearchCriteria } from 'api/accidents/models/AccidentsSearchCriteria';
import AccidentFiltersScreen, { Filters as FiltersAccidentsModel } from 'screens/vehicle/components/carAccidentTab/components/AccidentFilters/AccidentFiltersScreen';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import InfoCard from 'common/components/infoCard/InfoCard';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import colors from 'styles/export/colors.module.scss';

export type CloseType = 'edit' | 'details';

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
    colors.counterBlue2,
    colors.counterBlue3,
];

const DriverScreen = () => {
    const { t } = useTranslation();
    const { driverId, type, tabId } = useParams<{ driverId: string, type: Types, tabId: string, itemType: string, itemId: string }>();

    const { addToast } = useToasts();
    const history = useHistory();

    const isDriver = !!driverId;
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const [criteriaUsers, setCriteriaUsers] = useState<DriversSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'u.real_name',
        orderBy: 'asc',
        driverId: isDriver ? driverId : undefined,
    });
    const [totals, setTotals] = useState<DriversTotalsDto>({ averageKilometers: 0, fines: 0, totalAccidents: 0, refoundsMade: 0, refundsMissing: 0, refundsNotPayed: 0, refundsPending: 0, totalCostRefundsDenied: 0 });
    const [filtersUsers, setFiltersUsers] = useState<FiltersDriversModel>({});
    const [filtersUsersTotal, setFiltersUsersTotal] = useState(0);

    const [criteriaCharges, setCriteriaCharges] = useState<ChargesSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        vehicleId: undefined,
        responsibleId: isDriver ? driverId : undefined,
        orderColumn: 'date',
        orderBy: 'desc',
        startDate: undefined,
        endDate: undefined,
        vehiclesInactive: true,
        translations:{},
        fuelTypeTranslations:{}
    });
    const [filtersCharges, setFiltersCharges] = useState<FiltersChargesModel>({ vehiclesInactive: true });
    const [filtersChargesTotal, setFiltersChargesTotal] = useState(0);

    const [criteriaAccidents, setCriteriaAccidents] = useState<AccidentsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        vehicleId: undefined,
        driverId: isDriver ? driverId : undefined,
        orderColumn: 'date',
        orderBy: 'desc',
    });
    const [filtersAccidents, setFiltersAccidents] = useState<FiltersAccidentsModel>({});
    const [filtersAccidentsTotal, setFiltersAccidentsTotal] = useState(0);

    const [forceRenderTab, setForceRenderTab] = useState(new Date());

    const navigateToTab = (tabId: string) => {
        history.replace(`/drivers/details/${driverId}/${tabId}/list`);
        updateTotalFiltersUsers(filtersUsers)
        updateTotalFiltersCharges(filtersCharges)
        updateTotalFiltersAccidents(filtersAccidents)
    }

    const getData = async () => {
        try {
            Loading.show();
            const _totals = await DriversService.getTotals(criteriaUsers);
            setTotals(_totals);
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.DRIVERS, `Couldn't get driver information `, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const reloadCounters = () => {
        getData();
    }

    const canAddDrivers = async () => {
        try {
            Loading.show();
            await UsersService.candAddUsers();
            Loading.hide();
        } catch (error) {
            if (error?.response?.status === 402) {
                addToast(t('common.errors.pack_limit_exceeded'), { appearance: 'warning' });
                history.push(`/account/details/subscription`);
            }
        }
    }


    const tabs: TabItem[] = useMemo(() => {
        const items: TabItem[] = [
            {
                id: 'user',
                title: t('drivers.user'),
                content: <DriverUserTab />
            },
            {
                id: 'costs',
                title: t('drivers.costs'),
                content: <DriverChargeTab criteriaProp={criteriaCharges} reloadCounters={reloadCounters} />
            },
            {
                id: 'accidents',
                title: t('drivers.accidents'),
                content: <DriverAccidentTab criteriaProp={{ ...criteriaAccidents }} />
            }
        ];
        return items;
    }, [criteriaCharges, criteriaAccidents]);

    useEffect(() => {

        if (!driverId) {
            canAddDrivers();
        }
        getData();
    }, [driverId, criteriaUsers]);

    const onChangeFiltersUsers = (f: FiltersDriversModel, forcedChange?: boolean) => {
        updateTotalFiltersUsers(f);
        setFiltersUsers(f);

        setCriteriaUsers(prev => ({
            ...prev,
            driverId: isDriver ? driverId : undefined,
            minRefunds: f.refunds && f.refunds[0],
            maxRefunds: f.refunds && f.refunds[1],
            minFines: f.fines && f.fines[0],
            maxFines: f.fines && f.fines[1],
            minAccidents: f.accidents && f.accidents[0],
            maxAccidents: f.accidents && f.accidents[1],
            vehicleId: f.vehicleId,
            startDate: f.startDate,
            endDate: f.endDate,
            page: 1,
        }));

        if (!forcedChange) {
            onChangeFiltersAccidents({ ...filtersAccidents, startDate: f.startDate, endDate: f.endDate, vehicleId: f.vehicleId, vehicleName: f.vehicleName }, true);
            onChangeFiltersCharges({ ...filtersCharges, startDate: f.startDate, endDate: f.endDate, vehicleId: f.vehicleId, vehicleName: f.vehicleName }, true);
            setForceRenderTab(new Date());
        }
    }

    const updateTotalFiltersUsers = (f: FiltersDriversModel) => {
        let total = 0;

        if (f.startDate || f.endDate) {
            total++;
        }
        if (f.driverId) {
            total++;
        }
        if (f.vehicleId) {
            total++;
        }
        if (f.fines) {
            total++;
        }
        if (f.accidents) {
            total++;
        }
        if (f.refunds) {
            total++;
        }

        setFiltersUsersTotal(total);
    }


    const onChangeFiltersCharges = (f: FiltersChargesModel, forcedChange?: boolean) => {
        updateTotalFiltersCharges(f);
        setFiltersCharges(f);
        setCriteriaCharges(prev => ({
            ...prev,
            responsibleId: f.responsibleId,
            startDate: f.startDate,
            endDate: f.endDate,
            vehicleId: f.vehicleId,
            typeId: f.typeId,
            fuelId: f.fuelId,
            supplierId: f.supplierId,
            minCost: f.cost && f.cost[0],
            maxCost: f.cost && f.cost[1],
            refundSituation: f.refundSituation,
            refundState: f.refundState,
            vehiclesInactive: f.vehiclesInactive,
            geofenceId: f.geofenceId,
            hasGeofence: f.hasGeofence,
            localIds: f.localIds,
            localOperator: f.localOperator,
            segmentId: f.segmentId,
            page: 1,
        }));

        if (!forcedChange) {
            onChangeFiltersUsers({ ...filtersUsers, startDate: f.startDate, endDate: f.endDate, vehicleId: f.vehicleId, vehicleName: f.vehicleName }, true);
            onChangeFiltersAccidents({ ...filtersAccidents, startDate: f.startDate, endDate: f.endDate, vehicleId: f.vehicleId, vehicleName: f.vehicleName }, true);
            setForceRenderTab(new Date());
        }
    }

    const updateTotalFiltersCharges = (f: FiltersChargesModel) => {
        let total = 0;

        if (f.startDate || f.endDate) {
            total++;
        }
        if (f.vehicleId && f.vehicleId.length > 0) {
            total++;
        }
        if (f.typeId && f.typeId.length > 0) {
            total++;
        }
        if (f.supplierId && f.supplierId.length > 0) {
            total++;
        }
        if (f.responsibleId && f.responsibleId.length > 0 && !isDriver) {
            total++;
        }
        if (f.refundSituation != null) {
            total++;
        }
        if (f.cost && (f.cost[0] > 0 || f.cost[1] > 0)) {
            total++;
        }

        if (f.refundState != null) {
            total++;
        }

        if (f.vehiclesInactive != null) {
            total++;
        }

        if (f.fuelId && f.fuelId.length > 0) {
            total++;
        }

        if (f.geofenceId) {
            total++;
        }

        if (f.hasGeofence != null) {
            total++;
        }

        if (f.segmentId) {
            total++;
        }

        if (f.localIds?.length) {
            total++;
        }

        setFiltersChargesTotal(total);
    }

    const onChangeFiltersAccidents = (f: FiltersAccidentsModel, forcedChange?: boolean) => {
        updateTotalFiltersAccidents(f);
        setFiltersAccidents(f);
        setCriteriaAccidents(prev => ({
            ...prev,
            driverId: isDriver ? driverId : undefined,
            startDate: f.startDate,
            endDate: f.endDate,
            guilty: f.guilty,
            allIn: f.allIn,
            vehicleId: f.vehicleId,
            insuranceAssumedCost: f.insuranceAssumedCost,
            page: 1,
        }));
        if (!forcedChange) {
            onChangeFiltersUsers({ ...filtersUsers, startDate: f.startDate, endDate: f.endDate, vehicleId: f.vehicleId, vehicleName: f.vehicleName }, true);
            onChangeFiltersCharges({ ...filtersCharges, startDate: f.startDate, endDate: f.endDate, vehicleId: f.vehicleId, vehicleName: f.vehicleName }, true);
            setForceRenderTab(new Date());
        }
    }

    const updateTotalFiltersAccidents = (f: FiltersAccidentsModel) => {
        let total = 0;

        if (f.startDate || f.endDate) {
            total++;
        }
        if (f.allIn) {
            total++;
        }
        if (f.driverId && !isDriver) {
            total++;
        }
        if (f.guilty === false || f.guilty === true) {
            total++;
        }
        if (f.vehicleId && f.vehicleId.length > 0) {
            total++;
        }
        if (f.insuranceAssumedCost === false || f.insuranceAssumedCost === true) {
            total++
        }

        setFiltersAccidentsTotal(total);
    }

    // Build filter badge items based on active tab
    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (tabId === 'user' || !tabId) {
            if (filtersUsers.vehicleName) {
                items.push({
                    key: 'vehicle',
                    label: t('vehicle.vehicle') as string,
                    value: filtersUsers.vehicleName,
                    onRemove: () => { onChangeFiltersUsers({ ...filtersUsers, vehicleId: undefined, vehicleName: undefined }) }
                });
            }
            if (filtersUsers.startDate || filtersUsers.endDate) {
                const start = filtersUsers.startDate ? dayjs(filtersUsers.startDate).format(DATE_FORMAT_DEFAUT) : '';
                const end = filtersUsers.endDate ? dayjs(filtersUsers.endDate).format(DATE_FORMAT_DEFAUT) : '';
                items.push({
                    key: 'dates',
                    label: t('drivers.filter.date') as string,
                    value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                    onRemove: () => { onChangeFiltersUsers({ ...filtersUsers, startDate: undefined, endDate: undefined }) }
                });
            }
        } else if (tabId === 'costs') {
            if (filtersCharges.vehicleName) {
                items.push({
                    key: 'vehicle',
                    label: t('vehicle.vehicle') as string,
                    value: filtersCharges.vehicleName,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, vehicleId: undefined, vehicleName: undefined }) }
                });
            }
            if (filtersCharges.typeName) {
                items.push({
                    key: 'type',
                    label: t('common.type') as string,
                    value: filtersCharges.typeName,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, typeId: undefined, typeName: undefined }) }
                });
            }
            if (filtersCharges.fuelName) {
                items.push({
                    key: 'fuel',
                    label: t('vehicle.fuel') as string,
                    value: filtersCharges.fuelName,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, fuelId: undefined, fuelName: undefined }) }
                });
            }
            if (filtersCharges.refundState != null) {
                items.push({
                    key: 'refundState',
                    label: t('charges.reimbursement_status') as string,
                    value: t(('charges.list.request_status_type.' + filtersCharges.refundState) as any) as string,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, refundState: undefined }) }
                });
            }
            if (filtersCharges.refundSituation != null) {
                items.push({
                    key: 'refundSituation',
                    label: t('charges.reimbursement_situation') as string,
                    value: t(('charges.situations.' + filtersCharges.refundSituation) as any) as string,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, refundSituation: undefined }) }
                });
            }
            if (filtersCharges.supplierName) {
                items.push({
                    key: 'supplier',
                    label: t('common.supplier') as string,
                    value: filtersCharges.supplierName,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, supplierId: undefined, supplierName: undefined }) }
                });
            }
            if (filtersCharges.cost?.filter(x => x !== undefined).length) {
                let value = '';
                if (filtersCharges.cost.filter(i => i !== undefined).length === 2) {
                    value = `${filtersCharges.cost[0]} - ${filtersCharges.cost[1]}`;
                } else if (filtersCharges.cost[0] !== undefined) {
                    value = `> ${filtersCharges.cost[0]}`;
                } else {
                    value = `< ${filtersCharges.cost[1]}`;
                }
                items.push({
                    key: 'cost',
                    label: t('common.cost') as string,
                    value,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, cost: undefined }) }
                });
            }
            if (filtersCharges.startDate || filtersCharges.endDate) {
                const start = filtersCharges.startDate ? dayjs(filtersCharges.startDate).format(DATE_FORMAT_DEFAUT) : '';
                const end = filtersCharges.endDate ? dayjs(filtersCharges.endDate).format(DATE_FORMAT_DEFAUT) : '';
                items.push({
                    key: 'dates',
                    label: t('charges.filter_date') as string,
                    value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, startDate: undefined, endDate: undefined }) }
                });
            }
            if (filtersCharges.vehiclesInactive) {
                items.push({
                    key: 'vehiclesInactive',
                    label: t('charges.vehicle_inactives') as string,
                    value: t('common.yes') as string,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, vehiclesInactive: false }) }
                });
            }
            if (filtersCharges.geofenceName) {
                items.push({
                    key: 'geofence',
                    label: t('charges.geofences.title') as string,
                    value: filtersCharges.geofenceName,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, geofenceId: undefined, geofenceName: undefined }) }
                });
            }
            if (filtersCharges.hasGeofence != undefined) {
                items.push({
                    key: 'hasGeofence',
                    label: t('charges.has_geofence') as string,
                    value: (filtersCharges.hasGeofence ? t('common.yes') : t('common.no')) as string,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, hasGeofence: undefined }) }
                });
            }
            if (filtersCharges.segmentName) {
                items.push({
                    key: 'segment',
                    label: t('vehicle.type') as string,
                    value: filtersCharges.segmentName,
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, segmentId: undefined, segmentName: undefined }) }
                });
            }
            if (filtersCharges.localNames?.length) {
                items.push({
                    key: 'local',
                    label: `${t('vehicle.local')} (${filtersCharges.localOperator === 'AND' ? t('common.filter_all' as any) : t('common.filter_any' as any)})` as string,
                    value: filtersCharges.localNames.join(', '),
                    onRemove: () => { onChangeFiltersCharges({ ...filtersCharges, localIds: [], localNames: [] }) }
                });
            }
        } else if (tabId === 'accidents') {
            if (filtersAccidents.vehicleName) {
                items.push({
                    key: 'vehicle',
                    label: t('vehicle.vehicle') as string,
                    value: filtersAccidents.vehicleName,
                    onRemove: () => { onChangeFiltersAccidents({ ...filtersAccidents, vehicleId: undefined, vehicleName: undefined }) }
                });
            }
            if (filtersAccidents.guilty != undefined) {
                items.push({
                    key: 'guilty',
                    label: t('accidents.accident.guilt') as string,
                    value: (filtersAccidents.guilty ? t('accidents.guilt_types.guilty') : t('accidents.guilt_types.innocent')) as string,
                    onRemove: () => { onChangeFiltersAccidents({ ...filtersAccidents, guilty: undefined }) }
                });
            }
            if (filtersAccidents.startDate || filtersAccidents.endDate) {
                const start = filtersAccidents.startDate ? dayjs(filtersAccidents.startDate).format(DATE_FORMAT_DEFAUT) : '';
                const end = filtersAccidents.endDate ? dayjs(filtersAccidents.endDate).format(DATE_FORMAT_DEFAUT) : '';
                items.push({
                    key: 'dates',
                    label: t('common.date') as string,
                    value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                    onRemove: () => { onChangeFiltersAccidents({ ...filtersAccidents, startDate: undefined, endDate: undefined }) }
                });
            }
            if (filtersAccidents.allIn) {
                items.push({
                    key: 'allIn',
                    label: t('accidents.list.text_fields_search') as string,
                    value: filtersAccidents.allIn,
                    onRemove: () => { onChangeFiltersAccidents({ ...filtersAccidents, allIn: undefined }) }
                });
            }
        }

        return items;
    }, [filtersUsers, filtersCharges, filtersAccidents, tabId, t, userProfile]);

    const activeFiltersTotal = tabId === 'costs' ? filtersChargesTotal
        : tabId === 'accidents' ? filtersAccidentsTotal
            : filtersUsersTotal;

    const renderCounterBoxes = () => (
        <div className={compactPageLayoutStyles.rowCounters}>
            <InfoCard
                label={t('drivers.list.refunds') as string}
                value={totals?.refoundsMade ?? 0}
                icon={CoinsIcon}
                backgroundColor={INFO_CARD_COLORS[0]}
                isMoney
            />
            <InfoCard
                label={t('drivers.list.refunds_pending') as string}
                subtitle={`${t('drivers.list.refunds_pending_tooltip')}: ${totals?.refundsNotPayed ?? 0}/${totals?.refundsPending ?? 0}`}
                value={totals?.refundsMissing ?? 0}
                icon={CoinsIcon}
                backgroundColor={INFO_CARD_COLORS[1]}
                isMoney
            />
            <InfoCard
                label={t('drivers.list.refunds_not_approved') as string}
                value={totals?.totalCostRefundsDenied ?? 0}
                icon={CoinsIcon}
                backgroundColor={INFO_CARD_COLORS[2]}
                isMoney
            />
            <InfoCard
                label={t('drivers.list.total_fines') as string}
                subtitle={`Nº: ${totals?.fines ?? 0}`}
                value={totals?.totalCostFines ?? 0}
                icon={TicketIcon}
                backgroundColor={INFO_CARD_COLORS[3]}
                isMoney
            />
            <InfoCard
                label={t('drivers.list.total_accidents') as string}
                value={totals?.totalAccidents ?? 0}
                icon={ConeIcon}
                backgroundColor={INFO_CARD_COLORS[4]}
            />
        </div>
    );

    return (
        <ScreenTitle title={t('drivers.title')}>
            <CompactPageLayout
                title={t('drivers.title_single') as string}
                contentAreaClassName={styles.contentArea}
                onBack={type ? () => history.goBack() : undefined}
                middleContent={isDriver ? <FilterBadgesOverflow items={filterItems} align="right" /> : undefined}
                headerButtons={
                    isDriver ? (
                        <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                            positions={['bottom', 'left']}
                            align={'end'}
                            onClickOutside={() => tabId == 'user' ? updateTotalFiltersUsers(filtersUsers)
                                : tabId == 'costs' ? updateTotalFiltersCharges(filtersCharges)
                                    : tabId == 'accidents' ? updateTotalFiltersAccidents(filtersAccidents) : undefined}
                            content={setIsPopoverOpen =>
                                tabId == 'user' ?
                                    <DriversFilters
                                        isDriver={isDriver}
                                        filters={filtersUsers}
                                        onFilter={f => { setIsPopoverOpen(false); onChangeFiltersUsers(f) }}
                                        onChange={f => updateTotalFiltersUsers(f)}
                                    />
                                    : tabId == 'costs' ?
                                        <ChargeFiltersScreen
                                            isVehicle={false}
                                            isDriver={isDriver}
                                            filters={filtersCharges}
                                            onFilter={f => { setIsPopoverOpen(false); onChangeFiltersCharges(f) }}
                                            onChange={f => updateTotalFiltersCharges(f)}
                                        />
                                        : tabId == 'accidents' ?
                                            <AccidentFiltersScreen
                                                isVehicle={false}
                                                isDriver={isDriver}
                                                filters={filtersAccidents}
                                                onFilter={f => { setIsPopoverOpen(false); onChangeFiltersAccidents(f) }}
                                                onChange={f => updateTotalFiltersAccidents(f)}
                                            />
                                            : <></>
                            }>
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                    {activeFiltersTotal > 0 &&
                                        <div className={styles.counterList}> <div className={styles.counterNumber}>{activeFiltersTotal}</div> </div>
                                    }
                                </ScreenHeaderButton>
                            )}
                        </Popover>
                    ) : undefined
                }
            >
                {totals && (
                    <div className={compactPageLayoutStyles.countersSection}>
                        {renderCounterBoxes()}
                    </div>
                )}

                <div className={styles.tabsWrapper}>
                    {<Tabs
                        key={forceRenderTab + '_tabs'}
                        items={tabs}
                        activeTabId={tabId ?? 'user'}
                        onChange={(tabId: string) => (!type || !!type && type === 'details') && navigateToTab(tabId)}
                    />}
                </div>
            </CompactPageLayout>
        </ScreenTitle>
    );
}

export default DriverScreen;
